<template>
  <div class="closet-page iabf-dancer-theme">
    <div class="closet-inner">
      <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
      <header class="closet-hero">
        <p class="iabf-caption">Borrow from us</p>
        <h1 class="iabf-display">Costume closet</h1>
        <p class="iabf-hint">
          Veils, crowns, props, character bits, and a few romantic tutus — you’re welcome to borrow.
          Headpieces and extras are $5; romantic tutus are $15. It goes on your IABF account
          as its own line, so you’ll always see exactly what you reserved.
        </p>
      </header>

      <div v-if="loading" class="muted">Loading the closet…</div>
      <p v-else-if="loadError" class="error" role="alert">{{ loadError }}</p>
      <p v-else-if="!grouped.length" class="muted">Nothing in the closet yet — check back soon.</p>

      <section v-for="group in grouped" :key="group.id" class="closet-group">
        <h2>{{ group.label }}</h2>
        <div class="closet-grid">
          <article v-for="item in group.items" :key="item.id" class="closet-card iabf-card">
            <div class="closet-photo" :class="{ 'is-empty': !item.photoUrl }">
              <img v-if="item.photoUrl" :src="item.photoUrl" :alt="item.name">
              <span v-else>Photo coming soon</span>
            </div>
            <h3>{{ item.name }}</h3>
            <p class="qty">
              {{ formatClosetPrice(closetItemPriceCents(item)) }}
              <span v-if="item.qty != null"> · {{ availableFor(item) }} left</span>
            </p>
            <p class="desc">{{ item.description }}</p>
            <div class="card-actions">
              <p v-if="myLoanFor(item)" class="reserved">
                Reserved for you
                <button type="button" class="text-btn" :disabled="busyId === item.id" @click="cancelItem(item)">
                  Cancel
                </button>
              </p>
              <p v-else-if="availableFor(item) === 0" class="sold-out">All reserved</p>
              <button
                v-else
                type="button"
                class="iabf-btn iabf-btn--primary"
                :disabled="busyId === item.id || !canBorrow"
                @click="borrowItem(item)"
              >
                {{ busyId === item.id ? 'Reserving…' : 'Borrow · ' + formatClosetPrice(closetItemPriceCents(item)) }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { CLOSET_CATEGORIES, CLOSET_LOAN_ACTIVE, closetItemPriceCents, formatClosetPrice } from '@/config/iabf-costume-closet'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'

export default {
  name: 'CostumeCloset',
  data () {
    return {
      loading: true,
      loadError: '',
      busyId: '',
      actionError: ''
    }
  },
  computed: {
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) || IABF_DANCER_YEAR
    },
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    canBorrow () {
      return !!(this.dancer && this.dancer.id && !this.dancer.isCanceledAccount)
    },
    items () {
      return (this.$store.getters.costumeCloset || []).filter(i => i.published !== false)
    },
    loans () {
      return this.$store.getters.closetLoans || []
    },
    grouped () {
      return CLOSET_CATEGORIES.map(cat => ({
        id: cat.id,
        label: cat.label,
        items: this.items.filter(i => i.category === cat.id)
      })).filter(g => g.items.length)
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    const user = this.$store.getters.user
    if ((!this.dancer || !this.dancer.id) && user && user.id) {
      this.$store.dispatch('loadIABFDancerByUid', user.id).catch(() => {})
    }
    this.refresh()
  },
  methods: {
    closetItemPriceCents,
    formatClosetPrice,
    myLoanFor (item) {
      const dancerId = this.dancer && this.dancer.id
      if (!dancerId) {
        return null
      }
      return this.loans.find(l =>
        l.itemId === item.id &&
        l.dancerId === dancerId &&
        CLOSET_LOAN_ACTIVE.indexOf(l.status) !== -1
      ) || null
    },
    availableFor (item) {
      const stock = item.qty == null ? null : Number(item.qty)
      if (stock == null || !Number.isFinite(stock)) {
        return null
      }
      const used = this.loans
        .filter(l => l.itemId === item.id && CLOSET_LOAN_ACTIVE.indexOf(l.status) !== -1)
        .reduce((sum, l) => sum + (Number(l.qty) || 1), 0)
      return Math.max(0, stock - used)
    },
    async refresh () {
      this.loading = true
      this.loadError = ''
      try {
        await Promise.all([
          this.$store.dispatch('loadCostumeCloset'),
          this.$store.dispatch('loadClosetLoans', this.festivalYear)
        ])
      } catch (e) {
        this.loadError = (e && e.message) || 'Could not load the closet.'
      }
      this.loading = false
    },
    callableMessage (e) {
      return (e && e.message) ? String(e.message).replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]+\)\s*$/, '') : 'Something went wrong.'
    },
    async borrowItem (item) {
      const price = formatClosetPrice(closetItemPriceCents(item))
      if (!window.confirm('Reserve “' + item.name + '” for ' + price + '? We’ll add it to your IABF account as its own closet charge.')) {
        return
      }
      this.busyId = item.id
      this.actionError = ''
      try {
        await this.$store.dispatch('borrowClosetItem', { itemId: item.id, year: this.festivalYear })
        await this.refresh()
      } catch (e) {
        this.actionError = this.callableMessage(e)
        window.alert(this.actionError)
      }
      this.busyId = ''
    },
    async cancelItem (item) {
      const loan = this.myLoanFor(item)
      if (!loan) {
        return
      }
      if (!window.confirm('Cancel your reservation for “' + item.name + '”?')) {
        return
      }
      this.busyId = item.id
      try {
        await this.$store.dispatch('cancelClosetLoan', { loanId: loan.id })
        await this.refresh()
      } catch (e) {
        window.alert(this.callableMessage(e))
      }
      this.busyId = ''
    }
  }
}
</script>

<style scoped>
.closet-page {
  padding: 16px 16px 64px;
}
.closet-inner {
  max-width: 880px;
  margin: 0 auto;
}
.back-link {
  display: inline-block;
  margin-bottom: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
  text-decoration: none;
}
.closet-hero {
  margin-bottom: 28px;
}
.closet-hero a {
  color: inherit;
}
.closet-group {
  margin-bottom: 32px;
}
.closet-group h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 22px;
  margin: 0 0 12px;
}
.closet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}
.closet-card {
  padding: 0;
  overflow: hidden;
}
.closet-card h3 {
  font-size: 16px;
  margin: 12px 14px 4px;
}
.qty {
  margin: 0 14px 6px;
  font-size: 13px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.desc {
  margin: 0 14px 10px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--iabf-ink-muted, #5c5a63);
}
.card-actions {
  padding: 0 14px 14px;
}
.reserved, .sold-out {
  margin: 0;
  font-size: 14px;
}
.reserved { color: var(--iabf-success, #5f7a55); }
.sold-out { color: var(--iabf-ink-faint, #8a8792); }
.text-btn {
  border: none;
  background: none;
  color: var(--iabf-ink-muted, #5c5a63);
  text-decoration: underline;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
}
.closet-photo {
  height: 160px;
  background: var(--iabf-bg-soft, #eef0eb);
  display: flex;
  align-items: center;
  justify-content: center;
}
.closet-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.closet-photo.is-empty {
  color: var(--iabf-ink-faint, #8a8792);
  font-size: 13px;
  font-style: italic;
}
.muted { color: var(--iabf-ink-muted, #5c5a63); }
.error { color: var(--iabf-danger, #b54a4a); }
</style>
